import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18n-lite'

import { useRecaptcha, useLogin } from 'authentication/shared/hooks'
import AuthenticationTemplate from 'authentication/shared/components/AuthenticationTemplate'
import Identification from './Identification'
import Authentication from './Authentication'
import MFA from './MFA'
import LoginContext from './LoginContext'
import {
  feedbackcard,
  useDisplayUserError
} from '@yes.technology/react-toolkit'
import Title from 'shared/components/Title'

const LoginContainer = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const isWebauthn = searchParams.get('webauthn') && true
  const { login } = useLogin()
  const { captcha, isCaptchaDisabled } = useRecaptcha()
  const navigate = useNavigate()
  const [inputMode, setInputMode] = useState('identification')

  const goToIdentification = () => setInputMode('identification')
  const goToAuthentication = () => setInputMode('authentication')
  const goToMfa = () => setInputMode('mfa')

  const [userID, setUserID] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')

  const navigateNextStep = () => {
    navigate(isWebauthn ? '/webauthn' : '/credentials')
  }

  const { displayUserError } = useDisplayUserError()

  const handleLogin = () => {
    const loginProps = {
      payload: {
        email: userID,
        password,
        captcha
      },
      onSuccess: () => {
        feedbackcard(t('login.feedback.success.title'), {
          message: t('login.feedback.success.msg'),
          type: 'success'
        })
        navigateNextStep()
      },
      onError: (message: string) => {
        if (message === 'mfa_required') {
          goToMfa()
          return
        }
        displayUserError({
          title: t('login.feedback.error.title'),
          userMessageFallback: t('login.feedback.error.msg'),
          error: { message }
        })
      }
    }

    login(loginProps)
  }

  const handleMfaLogin = () => {
    const loginProps = {
      payload: {
        email: userID,
        password,
        captcha,
        mfa_code: mfaCode
      },
      onSuccess: () => {
        feedbackcard(t('login.feedback.success.title'), {
          message: t('login.feedback.success.msg'),
          type: 'success'
        })
        navigateNextStep()
      },
      onError: (message: string) => {
        // Erro de MFA ou de senha são indistinguíveis (mensagem genérica).
        // Volta para a etapa de senha e limpa ambos os campos para nova tentativa.
        setPassword('')
        setMfaCode('')
        goToAuthentication()
        displayUserError({
          title: t('login.mfa.feedback.error.title'),
          userMessageFallback: t('login.mfa.feedback.error.msg'),
          error: { message }
        })
      }
    }

    login(loginProps)
  }

  const isIdentification = inputMode === 'identification'
  const isAuthentication = inputMode === 'authentication'
  const isMfa = inputMode === 'mfa'

  const titleKey = isIdentification
    ? 'login.identification.title'
    : isMfa
      ? 'login.mfa.title'
      : 'login.authentication.title'

  return (
    <LoginContext.Provider
      value={{
        userID,
        setUserID,
        password,
        setPassword,
        captcha,
        isCaptchaDisabled,
        handleLogin,
        mfaCode,
        setMfaCode,
        handleMfaLogin
      }}
    >
      <AuthenticationTemplate>
        <Title>{t(titleKey)}</Title>
        {isIdentification && (
          <Identification goToAuthentication={goToAuthentication} />
        )}
        {isAuthentication && (
          <Authentication goToIdentification={goToIdentification} />
        )}
        {isMfa && <MFA goToAuthentication={goToAuthentication} />}
      </AuthenticationTemplate>
    </LoginContext.Provider>
  )
}

export default LoginContainer
